/**
 * ProgressProvider — initializes the progress store from Telegram user data
 * and keeps it synced with Supabase.
 *
 * Mount this once, wrapping the entire app content (inside <Router>).
 *
 * Responsibilities:
 *   1. Read Telegram user (id + first_name) → call store.init()
 *   2. Presence heartbeat while the mini app is visible
 *   3. Heartbeat: sync to Supabase every 5 minutes if there are pending seconds
 *   4. Sync on page unload (beforeunload)
 */

import { useEffect } from 'react'
import { useAuth } from '../context/AuthContext'
import { useProgressStore } from '../context/progressStore'

const HEARTBEAT_MS = 5 * 60 * 1000   // 5 minutes
const PRESENCE_MS = 180 * 1000       // 3 minutes

const ProgressProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth()
  const { init, syncToSupabase, pendingFocusSeconds, pingPresence } = useProgressStore()

  // ── 1. Initialize store when Telegram user is available ─────────────────
  useEffect(() => {
    if (user?.id) {
      if (import.meta.env.DEV) console.time('⏱️ ProgressStore_Init')
      init(user.id, user.first_name, user.username).then(() => {
        if (import.meta.env.DEV) console.timeEnd('⏱️ ProgressStore_Init')
      })
    }
  }, [user?.id])  // re-run only if the user id changes

  // ── 2. Presence heartbeat while mini app is visible ─────────────────────
  useEffect(() => {
    if (!user?.id) return

    const tick = () => {
      if (document.visibilityState === 'visible') {
        pingPresence()
      }
    }

    tick()
    const id = setInterval(tick, PRESENCE_MS)
    document.addEventListener('visibilitychange', tick)

    return () => {
      clearInterval(id)
      document.removeEventListener('visibilitychange', tick)
    }
  }, [user?.id, pingPresence])

  // ── 3. Heartbeat sync every 5 minutes ───────────────────────────────────
  useEffect(() => {
    const id = setInterval(() => {
      if (pendingFocusSeconds > 0) {
        syncToSupabase()
      }
    }, HEARTBEAT_MS)
    return () => clearInterval(id)
  }, [pendingFocusSeconds, syncToSupabase])

  // ── 4. Sync on page unload ───────────────────────────────────────────────
  useEffect(() => {
    const onUnload = () => syncToSupabase()
    window.addEventListener('beforeunload', onUnload)
    return () => window.removeEventListener('beforeunload', onUnload)
  }, [syncToSupabase])

  return <>{children}</>
}

export default ProgressProvider
